import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getListReturnPath, openRecord } from '../../hooks/useNavigateBack';
import { usePersistedListFilters } from '../../hooks/usePersistedListFilters';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { cn } from '../../lib/utils';
import type { ListQueryOptions } from '../../services/paginatedList';

export type ReferenceInboxColumn<T> = {
  key: string;
  header: string;
  sortable?: boolean;
  className?: string;
  align?: 'left' | 'right' | 'center';
  render: (row: T) => React.ReactNode;
};

export type ReferenceInboxModuleConfig = {
  module: {
    title: string;
    permissionModuleKey: string;
    route: {
      list: string;
      new: string;
      detail: (id: number) => string;
      edit: (id: number) => string;
    };
  };
  search: { placeholder: string };
  emptyStates: {
    noData: string;
    noSearchResults: string;
    noFilterResults?: string;
  };
  delete: {
    title: string;
    confirmText: string;
    cancelText: string;
    errorMessage: string;
    handler: (id: number) => Promise<unknown>;
  };
};

type BoardTab = 'active' | 'inactive' | 'all';

type UseDataResult<T> = {
  data: T[];
  total: number;
  isLoading: boolean;
  mutate: () => Promise<unknown> | unknown;
};

type ReferenceDataGeneralListProps<T extends { id: number }> = {
  moduleConfig: ReferenceInboxModuleConfig;
  useData: (
    search?: string,
    page?: number,
    limit?: number,
    options?: ListQueryOptions,
  ) => UseDataResult<T>;
  columns: ReferenceInboxColumn<T>[];
  defaultSort?: { key: string; direction: 'asc' | 'desc' };
  newLabel?: string;
  getRowLabel?: (row: T) => string;
  /** When false, hide Active/Inactive tabs and do not send isActive filter. Default true. */
  activeTabs?: boolean;
  /** Per-row delete gate (in addition to permission). */
  canDeleteRow?: (row: T) => boolean;
  /** Per-row edit gate (in addition to permission). */
  canEditRow?: (row: T) => boolean;
  /** Extra filter values merged into list query (e.g. kindId, columnType). */
  extraFilters?: Record<string, string[]>;
  tabTrailing?: React.ReactNode;
  /** Extra controlled filters that count toward hasFilters / clear. */
  hasExtraFilters?: boolean;
  onClearExtraFilters?: () => void;
  loadingLabel?: string;
};

const viewTabs: Array<{
  id: BoardTab;
  label: string;
  icon: React.ReactNode;
}> = [
  { id: 'active', label: 'Active', icon: <CheckCircleIcon className="h-4 w-4" /> },
  { id: 'inactive', label: 'Inactive', icon: <FolderIcon className="h-4 w-4" /> },
  { id: 'all', label: 'All', icon: <DocumentTextIcon className="h-4 w-4" /> },
];

export function ReferenceDataGeneralList<T extends { id: number }>({
  moduleConfig,
  useData,
  columns,
  defaultSort = { key: 'name', direction: 'asc' },
  newLabel,
  getRowLabel,
  activeTabs = true,
  canDeleteRow,
  canEditRow,
  extraFilters,
  tabTrailing,
  hasExtraFilters = false,
  onClearExtraFilters,
  loadingLabel,
}: ReferenceDataGeneralListProps<T>) {
  const { colors, theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const listReturnPath = getListReturnPath(location);
  const confirm = useConfirm();
  const { can } = useAuth();
  const permissionKey = moduleConfig.module.permissionModuleKey;
  const canEdit = can(permissionKey, 'edit');
  const canDelete = can(permissionKey, 'delete');
  const isDark = theme === 'dark';

  const storageKey = getListStateStorageKey(moduleConfig.module.route.list);
  const [persisted, setPersisted] = usePersistedListFilters<{ tab: BoardTab; search: string }>(storageKey);

  const [tab, setTab] = useState<BoardTab>(() =>
    activeTabs ? (persisted?.tab ?? 'active') : 'all',
  );
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState(() => persisted?.search ?? '');
  const [search, setSearch] = useState(() => persisted?.search ?? '');

  useEffect(() => {
    setPersisted({ tab, search });
  }, [tab, search, setPersisted]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { sortConfig, handleSort } = useGeneralListSort(defaultSort, {
    defaultDirectionForKey: (key) => (key === 'createdAt' || key === 'updatedAt' ? 'desc' : 'asc'),
  });

  useEffect(() => {
    setPage(1);
  }, [search, tab, itemsPerPage, sortConfig.key, sortConfig.direction, extraFilters]);

  const listOptions = useMemo(() => {
    const filters: Record<string, string[]> = { ...(extraFilters ?? {}) };
    if (activeTabs) {
      if (tab === 'active') filters.isActive = ['Yes'];
      if (tab === 'inactive') filters.isActive = ['No'];
    }
    return {
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
      filters: Object.keys(filters).length ? { filters } : undefined,
    };
  }, [tab, sortConfig, extraFilters, activeTabs]);

  const { data, isLoading, total, mutate } = useData(
    search || undefined,
    page,
    itemsPerPage,
    listOptions,
  );

  const totalPages = Math.max(1, Math.ceil((total ?? 0) / itemsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const hasFilters = Boolean(
    search.trim() || hasExtraFilters || (activeTabs && tab !== 'all'),
  );

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    if (activeTabs) setTab('all');
    onClearExtraFilters?.();
  };

  const addLabel = newLabel ?? `New ${moduleConfig.module.title.replace(/s$/i, '').toLowerCase()}`;

  const handleDelete = async (e: React.MouseEvent, row: T) => {
    e.stopPropagation();
    const label = getRowLabel?.(row) ?? (row as { name?: string }).name ?? `#${row.id}`;
    const ok = await confirm({
      title: moduleConfig.delete.title,
      message: `Delete “${label}”?`,
      confirmText: moduleConfig.delete.confirmText,
      cancelText: moduleConfig.delete.cancelText,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await moduleConfig.delete.handler(row.id);
      await mutate();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      window.alert(msg || moduleConfig.delete.errorMessage);
    }
  };

  return (
    <GeneralList
      title={activeTabs ? undefined : moduleConfig.module.title}
      tabs={activeTabs ? viewTabs.map(({ id, label, icon }) => ({ id, label, icon })) : undefined}
      activeTab={activeTabs ? tab : undefined}
      onTabChange={activeTabs ? setTab : undefined}
      sorting={{ sortConfig, onSort: handleSort }}
      tabTrailing={tabTrailing}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: moduleConfig.search.placeholder,
      }}
      actions={
        canEdit ? (
          <Link
            to={moduleConfig.module.route.new}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            {addLabel}
          </Link>
        ) : null
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel={loadingLabel ?? `Loading ${moduleConfig.module.title.toLowerCase()}…`}
      isEmpty={!isLoading && data.length === 0}
      emptyState={{
        icon: <DocumentTextIcon className="h-12 w-12" />,
        title: hasFilters
          ? moduleConfig.emptyStates.noSearchResults
          : moduleConfig.emptyStates.noData.replace(/^Create |^Add /i, 'No ').replace(/ to get started\.?$/i, ' yet'),
        hint: hasFilters
          ? moduleConfig.emptyStates.noFilterResults ?? 'Try adjusting filters or clearing the search.'
          : moduleConfig.emptyStates.noData,
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : canEdit ? (
          <Link
            to={moduleConfig.module.route.new}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            {addLabel}
          </Link>
        ) : undefined,
      }}
      pagination={{
        page,
        totalPages,
        total: total ?? 0,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className={cn('sticky top-0 z-10 w-px whitespace-nowrap font-medium', colors.surface)} />
            {columns.map((col) =>
              col.sortable !== false ? (
                <GeneralListSortableTh
                  key={col.key}
                  sortKey={col.key}
                  className={cn('whitespace-nowrap', col.className)}
                  align={col.align}
                >
                  {col.header}
                </GeneralListSortableTh>
              ) : (
                <th
                  key={col.key}
                  className={cn(
                    'sticky top-0 z-10 whitespace-nowrap font-medium',
                    colors.surface,
                    colors.textMuted,
                    col.className,
                  )}
                >
                  {col.header}
                </th>
              ),
            )}
            <th className={cn('sticky top-0 z-10 w-full font-medium', colors.surface)} aria-hidden />
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr
              key={row.id}
              onClick={() => openRecord(navigate, moduleConfig.module.route.detail(row.id), listReturnPath)}
              className={cn(
                'cursor-pointer border-b transition-colors last:border-b-0',
                colors.border,
                colors.text,
                colors.interactiveHover,
              )}
            >
              <td className="w-px whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-nowrap items-center">
                  <Link
                    to={moduleConfig.module.route.detail(row.id)}
                    state={{ from: listReturnPath }}
                    className={cn(
                      'rounded-lg p-1 transition-colors',
                      colors.primaryFg,
                      colors.interactiveHover,
                    )}
                    title="View"
                    aria-label="View"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </Link>
                  {canEdit && (canEditRow?.(row) ?? true) ? (
                    <Link
                      to={moduleConfig.module.route.edit(row.id)}
                      state={{ from: listReturnPath }}
                      className={cn(
                        'rounded-lg p-1 transition-colors',
                        colors.primaryFg,
                        colors.interactiveHover,
                      )}
                      title="Edit"
                      aria-label="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                  ) : null}
                  {canDelete && (canDeleteRow?.(row) ?? true) ? (
                    <button
                      type="button"
                      onClick={(e) => handleDelete(e, row)}
                      className={cn(
                        'rounded-lg p-1 transition-colors',
                        isDark
                          ? 'text-red-400 hover:bg-red-950/40'
                          : 'text-red-600 hover:bg-red-50',
                      )}
                      title="Delete"
                      aria-label="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  ) : null}
                </div>
              </td>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={cn('whitespace-nowrap pr-6', colors.text, col.className)}
                >
                  {col.render(row)}
                </td>
              ))}
              <td className="w-full" aria-hidden />
            </tr>
          ))}
        </tbody>
      </table>
    </GeneralList>
  );
}

export default ReferenceDataGeneralList;
